/**
 * `orange-masthead` — the eShop header. Prototype `masthead()`.
 *
 * ### The logo is a dashed placeholder on purpose
 *
 * The Orange mark is not reconstructed here and no Orange webfont is licensed for
 * this POC (`design-system/README.md`, `index.html`). A word set in the brand fill
 * with a dashed border is unmistakably a stand-in; a plausible-looking square
 * orange logotype would be mistaken for the real mark in a screenshot.
 *
 * ### The nav is not functional
 *
 * Business, Abonamente, Telefoane, Soluții, Suport are the real eShop's top level
 * and none of them belong to this flow. They are rendered as the surrounding site
 * so the flow does not look like it lives on its own domain, but they lead nowhere
 * — so they are `<span>`s, not links with `href="#"`. A link that does nothing is
 * a tab stop that lies to a keyboard user, and there are five of them.
 *
 * `Abonamente` carries `aria-current="page"` because that is the section this flow
 * sits inside.
 */
import { ChangeDetectionStrategy, Component, computed, inject } from '@angular/core';
import { SessionStore } from '../../core/stores/session.store';
import { IconComponent } from '../../ui/icon/icon.component';

interface NavItem {
  readonly label: string;
  readonly current: boolean;
}

const NAV: readonly NavItem[] = [
  { label: $localize`:@@nav.business:Business`, current: false },
  { label: $localize`:@@nav.plans:Abonamente`, current: true },
  { label: $localize`:@@nav.devices:Telefoane`, current: false },
  { label: $localize`:@@nav.solutions:Soluții`, current: false },
  { label: $localize`:@@nav.support:Suport`, current: false },
];

@Component({
  selector: 'orange-masthead',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [IconComponent],
  template: `
    <header class="masthead ground-inverse">
      <span
        class="logo"
        title="Logo placeholder — the Orange mark is not reconstructed in this POC"
        >orange</span
      >

      <nav class="masthead-nav" [attr.aria-label]="navLabel">
        @for (item of nav; track item.label) {
          <span [attr.aria-current]="item.current ? 'page' : null">{{ item.label }}</span>
        }
      </nav>

      @if (identity(); as who) {
        <div class="masthead-acc">
          <orange-icon name="user" size="sm" />
          <span class="who">
            <span>{{ who.name }}</span>
            <span class="co">{{ who.company }}</span>
          </span>
        </div>
      }
    </header>
  `,
  styles: [
    `
      .masthead {
        background: var(--surface-inverse);
        color: var(--surface);
        display: flex;
        align-items: center;
        gap: var(--space-3);
        padding: var(--space-2) var(--gutter);
        min-height: 56px;
      }

      .logo {
        background: var(--brand);
        color: var(--ink);
        font-weight: 700;
        font-size: 16px;
        line-height: 24px;
        padding: var(--space-2) var(--space-3);
        /* Dashed, and stays dashed. See the note at the top of the file. */
        border: var(--border-hairline) dashed var(--ink);
      }

      .masthead-nav {
        display: flex;
        gap: var(--space-4);
        flex: 1 1 auto;
        flex-wrap: wrap;
      }

      .masthead-nav span {
        font-size: 11px;
        line-height: 16px;
        font-weight: 700;
        color: var(--surface);
        padding: var(--space-2) 0;
        display: inline-flex;
        align-items: center;
        min-height: 44px;
      }

      .masthead-nav span[aria-current='page'] {
        border-bottom: 2px solid var(--brand);
      }

      .masthead-acc {
        display: flex;
        align-items: center;
        gap: var(--space-2);
        font-size: 11px;
        line-height: 16px;
        font-weight: 700;
      }

      .who {
        display: flex;
        flex-direction: column;
        text-align: right;
      }

      /* 400 weight and --grey-400 on the inverse ground: the company line is
         secondary to the name, and --grey-400 on --surface-inverse is the lightest
         grey that still clears AA against black. */
      .co {
        font-weight: 400;
        color: var(--grey-400);
      }

      /* The nav is the first thing to go — the account block identifies which
         company the order is being placed for and cannot be dropped. */
      @media (max-width: 720px) {
        .masthead-nav {
          display: none;
        }
      }
    `,
  ],
})
export class MastheadComponent {
  private readonly session = inject(SessionStore);

  readonly nav = NAV;
  readonly navLabel = $localize`:@@nav.label:Navigație principală`;

  readonly identity = computed(() => {
    const business = this.session.business();
    const name = this.session.displayName();
    if (!name) return null;
    return {
      name,
      // CUI alongside the trade name: on a shared account the administrator needs
      // to see which legal entity the order lands on, and two companies in the
      // same group often differ only by CUI.
      company: business ? `${business.companyName} · ${business.cui}` : '',
    };
  });
}
